/**
 * useSpeechNarration — Web Speech API wrapper for the Live Classroom.
 *
 * Powers two features at once:
 *   • Karaoke Mode: emits the (start, end) char index of the currently
 *     spoken word so the OAK-Pad can highlight it in real-time.
 *   • Lip-sync: exposes `speaking` + the current word for the
 *     <VideoInstructor /> to drive its mouth viseme state machine.
 *
 * Sentence chunking (CRITICAL)
 *   Chrome / Chromium has a 15-year-old documented bug where utterances
 *   longer than ~200 characters silently fail after ~15 seconds — the
 *   speech just stops with `error: synthesis-failed`. Our lesson Parts
 *   are ~850-2000 words (5000-12000 chars), well past that limit.
 *
 *   Fix: split the text into sentence-sized chunks (capped at 220 chars)
 *   and queue them sequentially via `onend` chaining. The user perceives
 *   a single continuous narration but each utterance the engine handles
 *   is small. Boundary events still fire per-chunk; we add a running
 *   `chunkOffset` so the reported char-index is relative to the FULL
 *   text — so Karaoke highlighting "just works".
 *
 *   See: https://bugs.chromium.org/p/chromium/issues/detail?id=679489
 *
 * Returns
 *   speak(text)         — start narrating (chunked internally)
 *   stop()              — cancel narration
 *   speaking            — bool, true while the utterance is live
 *   currentWord         — string, last word that fired a boundary event
 *   wordRange           — [start, end] char index in the FULL text
 *   supported           — bool, false on browsers without SpeechSynthesis
 */
import { useCallback, useEffect, useRef, useState } from "react";

// Voice priority — ordered HIGH→LOW perceived quality. Cloud Neural
// voices (Microsoft Online Natural — Aria/Jenny/Ava/Emma) sound far
// more human than the older offline ones (Zira/Hazel). On platforms
// without those, we fall back to Apple Premium → Google → local.
const FEMALE_VOICE_PRIORITY = [
  "Microsoft Aria Online (Natural) - English (United States)",
  "Microsoft Jenny Online (Natural) - English (United States)",
  "Microsoft Ava Online (Natural) - English (United States)",
  "Microsoft Emma Online (Natural) - English (United States)",
  "Microsoft Sonia Online (Natural) - English (United Kingdom)",
  "Microsoft Libby Online (Natural) - English (United Kingdom)",
  "Microsoft Natasha Online (Natural) - English (Australia)",
  "Microsoft Clara Online (Natural) - English (Canada)",
  "Microsoft Aria", "Microsoft Jenny", "Microsoft Ava", "Microsoft Emma",
  "Microsoft Sonia", "Microsoft Libby", "Microsoft Natasha", "Microsoft Clara",
  "Ava (Premium)", "Allison (Premium)", "Samantha (Premium)", "Karen (Premium)",
  "Siri Female (en-US)", "Siri Voice 1 (en-US)", "Siri Voice 4 (en-US)",
  "Ava", "Allison", "Samantha", "Karen", "Susan", "Moira", "Tessa",
  "Google UK English Female", "Google US English",
  "Microsoft Zira - English (United States)",
  "Microsoft Hazel - English (Great Britain)",
  "Microsoft Zira", "Microsoft Hazel",
];

function _isLikelyFemale(v) {
  const n = (v?.name || "").toLowerCase();
  if (/female/.test(n)) return true;
  return /(samantha|ava|allison|susan|karen|moira|tessa|aria|jenny|zira|serena|fiona|kate|amelia|sonia|libby|natasha|olivia)/i.test(n);
}

function _pickFemaleVoice(voices, lang) {
  if (!voices?.length) return null;
  const langPrefix = (lang || "en-US").split("-")[0];
  const sameLang = (v) => {
    const vl = v?.lang;
    if (!vl || typeof vl !== "string") return false;
    return vl === lang || vl.startsWith(langPrefix);
  };
  try {
    for (const name of FEMALE_VOICE_PRIORITY) {
      const m = voices.find((v) => v?.name === name && sameLang(v));
      if (m) return m;
    }
    const femaleSameLang = voices.filter((v) => sameLang(v) && _isLikelyFemale(v));
    if (femaleSameLang.length) return femaleSameLang[0];
    const anyFemale = voices.find(_isLikelyFemale);
    if (anyFemale) return anyFemale;
    return voices.find(sameLang) || voices[0] || null;
  } catch {
    return null;
  }
}

// Maximum characters per utterance. Chromium starts dropping out
// around 200; 220 gives a small safety margin while keeping chunks
// short enough to mostly fit a single sentence.
const MAX_CHUNK_CHARS = 220;

/**
 * Split text into ≤MAX_CHUNK_CHARS pieces, prefering sentence
 * boundaries. Returns [{text, offset}] where offset is the char
 * index of the chunk's first character in the ORIGINAL text — used
 * to translate boundary-event indices back to the full-text space.
 */
function _chunkSentences(text) {
  const out = [];
  if (!text) return out;
  // Sentence-ish split: . ! ? followed by whitespace, OR newlines.
  // Keep the terminator with the preceding sentence by capturing it.
  const sentences = text
    .split(/(?<=[.!?])\s+|\n+/g)
    .map((s) => s.trim())
    .filter(Boolean);

  let cursor = 0; // char index in original text
  for (const s of sentences) {
    // Resync cursor against the original (whitespace between
    // sentences may have been collapsed). Find the next occurrence
    // of `s` starting from `cursor`.
    const found = text.indexOf(s, cursor);
    const offset = found >= 0 ? found : cursor;
    if (s.length <= MAX_CHUNK_CHARS) {
      out.push({ text: s, offset });
      cursor = offset + s.length;
      continue;
    }
    // Sentence longer than the cap — break on commas / clause
    // boundaries / spaces. Greedy fill up to MAX_CHUNK_CHARS.
    let i = 0;
    while (i < s.length) {
      let end = Math.min(i + MAX_CHUNK_CHARS, s.length);
      if (end < s.length) {
        // Prefer to break at the last space within window.
        const lastSpace = s.lastIndexOf(" ", end);
        if (lastSpace > i + 80) end = lastSpace;
      }
      out.push({ text: s.slice(i, end).trim(), offset: offset + i });
      i = end;
    }
    cursor = offset + s.length;
  }
  return out;
}

export default function useSpeechNarration({ rate = 0.92, pitch = 1.08, lang = "en-US" } = {}) {
  const [speaking, setSpeaking] = useState(false);
  const [currentWord, setCurrentWord] = useState("");
  const [wordRange, setWordRange] = useState(null);
  const queueRef = useRef({ chunks: [], idx: 0, voice: null, cancelled: false });

  const supported =
    typeof window !== "undefined" &&
    typeof window.speechSynthesis !== "undefined" &&
    typeof window.SpeechSynthesisUtterance !== "undefined";

  // Warm the voice catalog. Chromium returns [] on the first sync
  // getVoices() call and only populates after `voiceschanged` fires.
  useEffect(() => {
    if (!supported) return;
    try { window.speechSynthesis.getVoices(); } catch { /* ignore */ }
    const reload = () => {
      try { window.speechSynthesis.getVoices(); } catch { /* ignore */ }
    };
    window.speechSynthesis.addEventListener?.("voiceschanged", reload);
    return () => {
      window.speechSynthesis.cancel();
      window.speechSynthesis.removeEventListener?.("voiceschanged", reload);
    };
  }, [supported]);

  // Speak ONE chunk in the queue. Each chunk creates a fresh utterance,
  // attaches handlers, and on `onend` advances to the next chunk.
  const _speakChunk = useCallback(() => {
    const q = queueRef.current;
    if (q.cancelled) return;
    if (q.idx >= q.chunks.length) {
      // All chunks done — fire global onend.
      setSpeaking(false);
      setCurrentWord("");
      setWordRange(null);
      return;
    }
    const chunk = q.chunks[q.idx];
    const utter = new window.SpeechSynthesisUtterance(chunk.text);
    utter.rate = rate;
    utter.pitch = pitch;
    utter.lang = lang;
    if (q.voice) utter.voice = q.voice;

    utter.onstart = () => {
      if (q.idx === 0) setSpeaking(true);
    };
    utter.onboundary = (ev) => {
      if (ev.name !== "word") return;
      const localStart = ev.charIndex ?? 0;
      const length = ev.charLength || _wordLength(chunk.text, localStart);
      // Translate back to the FULL-text char space so Karaoke
      // highlighting points at the right span.
      const globalStart = chunk.offset + localStart;
      setCurrentWord(chunk.text.slice(localStart, localStart + length));
      setWordRange([globalStart, globalStart + length]);
    };
    utter.onend = () => {
      q.idx += 1;
      // Tiny gap helps Chrome flush the engine between chunks.
      setTimeout(_speakChunk, 40);
    };
    utter.onerror = (ev) => {
      // eslint-disable-next-line no-console
      console.warn("[oak-tts] onerror", ev?.error || ev, "chunkIdx=", q.idx);
      // 'interrupted' fires when the user manually stops; don't
      // restart. Anything else: try to move on to the next chunk
      // so a single bad chunk doesn't kill the whole narration.
      if (ev?.error === "interrupted" || ev?.error === "canceled") {
        setSpeaking(false);
        setCurrentWord("");
        setWordRange(null);
        return;
      }
      q.idx += 1;
      setTimeout(_speakChunk, 80);
    };
    try {
      window.speechSynthesis.speak(utter);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[oak-tts] speak() threw", e);
      q.idx += 1;
      setTimeout(_speakChunk, 80);
    }
  }, [rate, pitch, lang]);

  const speak = useCallback(
    (text) => {
      if (!supported || !text?.trim()) {
        // eslint-disable-next-line no-console
        console.debug("[oak-tts] speak() skipped — supported=%s textLen=%s", supported, text?.length || 0);
        return;
      }
      // Cancel any in-flight utterance first.
      window.speechSynthesis.cancel();
      // Pick a voice once for the whole utterance.
      let voice = null;
      try {
        const voices = window.speechSynthesis.getVoices() || [];
        voice = _pickFemaleVoice(voices, lang);
        // eslint-disable-next-line no-console
        console.debug("[oak-tts] voices=%d picked=%s", voices.length, voice?.name || "(default)");
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[oak-tts] voice picker failed", e);
      }
      const chunks = _chunkSentences(text);
      // eslint-disable-next-line no-console
      console.debug("[oak-tts] chunked %d chars into %d chunks", text.length, chunks.length);
      queueRef.current = { chunks, idx: 0, voice, cancelled: false };
      // Small delay after cancel() — Chromium needs a tick to flush
      // the previous queue before accepting a new utterance, otherwise
      // the first speak() silently no-ops.
      setTimeout(_speakChunk, 80);
    },
    [supported, lang, _speakChunk]
  );

  const stop = useCallback(() => {
    if (!supported) return;
    queueRef.current.cancelled = true;
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setCurrentWord("");
    setWordRange(null);
  }, [supported]);

  return { speak, stop, speaking, currentWord, wordRange, supported };
}

// Fallback when the browser doesn't pass charLength (Safari quirk).
function _wordLength(text, start) {
  const m = text.slice(start).match(/^\S+/);
  return m ? m[0].length : 0;
}
